import type { TFunction } from 'i18next';
import type { PermissionCatalogueEntry, RoleCatalogueEntry } from '@config';

import type { PermissionAction } from '@pmp/api';
import { ROLE_DEFINITIONS, PERMISSIONS, toPermissionSet } from './utils';
import type { Role } from './interfaces';

/**
 * The permissions this application declares, with what each one lets a person do. Built from
 * `PERMISSIONS` so a permission added in code appears without a description rather than not at all.
 */
export const buildPermissionCatalogue = (t: TFunction): PermissionCatalogueEntry[] =>
  Object.entries(PERMISSIONS).flatMap(([resource, actions]) =>
    actions.map(action => ({
      resource,
      resourceLabel: t(`pmp.permissions.resources.${resource}`, { defaultValue: resource }),
      action,
      description: t(`pmp.permissions.descriptions.${resource}.${action}`, { defaultValue: '' }) || undefined,
    })),
  );

/**
 * The roles this application ships, with their permissions expressed in the same vocabulary as
 * the permission catalogue. The API reports resources as `BASE_RATES` and actions as `VIEW`, and
 * a few resources are named differently there, so `toPermissionSet` does the translation.
 */
export const buildRoleCatalogue = (
  t: TFunction,
  groupPermissions?: Record<string, Record<string, PermissionAction[]>>,
): RoleCatalogueEntry[] =>
  Object.entries(groupPermissions ?? {}).map(([role, resources]) => {
    const definition = ROLE_DEFINITIONS[role as Role];

    return {
      role,
      permissions: [...toPermissionSet(resources)].sort((a, b) => a.localeCompare(b)),
      group: definition ? t(`profile.groups.${definition.group}`, { defaultValue: definition.group }) : undefined,
      description: definition?.description ? t(definition.description, { defaultValue: '' }) || undefined : undefined,
      deprecated: definition?.deprecated,
      // Every role in this application is shipped with it. Client owned roles arrive with the editor.
      provided: true,
    };
  }).sort((a, b) => a.role.localeCompare(b.role));

/** What each feature flag turns on. A flag with no entry here is shown without a description. */
export const buildFlagDescriptions = (t: TFunction): Record<string, string> =>
  t('pmp.featureFlags.descriptions', { returnObjects: true, defaultValue: {} }) as Record<string, string>;
